import { createFileRoute, redirect } from '@tanstack/react-router';
import {
  canAccessManagement,
  canImpersonate,
  MANAGEMENT_DEFAULT_ROUTE,
  MANAGEMENT_FALLBACK_ROUTE,
  MANAGEMENT_ROOT,
} from '@/lib/utils/managementAccess';
import { bootstrapSession } from '@/lib/utils/session';
import { useSessionStore } from '@/store/sessionStore';

/**
 * Root of the internal management area. It holds nothing of its own — each tool
 * is a self-contained feature folder underneath it.
 *
 * What this route contributes is the gate. `beforeLoad` runs top-down, so every
 * feature added under here inherits it and none of them can ship unguarded.
 * See `canAccessManagement`: Retraced staff only, and only while acting as
 * themselves.
 */
export const Route = createFileRoute('/management')({
  beforeLoad: async ({ location }) => {
    // The root guard has already authenticated the request and bootstrapped the
    // session, so the store is normally warm. The fetch is the fallback for the
    // one case where it isn't: HMR swapping this module in without a navigation.
    const user = useSessionStore.getState().user ?? (await bootstrapSession());
    if (!canAccessManagement(user)) throw redirect({ to: '/dashboard' });

    // `/management` holds no page of its own, so it forwards to the tool an
    // operator came to use. It is a real destination — ending an impersonation
    // lands here, as does an operator bounced out of the customer app. Staff who
    // may not impersonate (see `canImpersonate`) are forwarded to CS Tools
    // instead, rather than to a page whose own guard would bounce them right
    // back out.
    if (
      location.pathname === MANAGEMENT_ROOT ||
      location.pathname === `${MANAGEMENT_ROOT}/`
    ) {
      throw redirect({
        to: canImpersonate(user)
          ? MANAGEMENT_DEFAULT_ROUTE
          : MANAGEMENT_FALLBACK_ROUTE,
      });
    }
  },
});
